import logging from '@common/logging';
import { ErrorPage } from '@common/pages/Error';

import { ConsolidatedModel } from '@common/models/ConsolidatedModel';
import params from '@common/params';
import security from '@common/security';
import support from '@common/support';
import tracking from '@common/tracking';
import React, { useEffect, useState } from 'react';
import { Route, Routes, useLocation, useRouteError } from 'react-router-dom';
import { ActivityController } from './account/activity/controllers/ActivityController';
import { AutoInvestmentsController } from './account/auto-investments/controllers/AutoInvestmentsController';
import { BankAccountsController } from './account/bank-accounts/controllers/BankAccountsController';
import { DocumentsController } from './account/documents/documents-list/controllers/DocumentsController';
import { StatementController } from './account/documents/statement/controllers/StatementController';
import { AccountInformationController } from './account/info/controllers/InfoController';
import { InvestorProfileController } from './account/profile/controllers/InvestorProfileController';
import { PartnerSignInController } from './auth/oidc/controllers/PartnerSignInController';
import { CheckoutCashflowController } from './checkout/cashflow/controllers/CheckoutCashflowController';
import { CheckoutSFRController } from './checkout/sfr/controllers/CheckoutSFRController';
import { ForbiddenPage } from './common/Forbidden';
import { PageNotFound } from './common/PageNotFound';
import { FullScreenLoader } from './common/components/FullScreenLoader';
import { ROUTES } from './config';
import { CreateAccountController } from './create-account/controllers/CreateAccountController';
import { DashboardController } from './dashboard/controllers/DashboardController';
import { ErrorController } from './error/controllers/ErrorController';
import { InboxController } from './inbox/controllers/InboxController';
import { PropertyController } from './property/controllers/PropertyController';
import { RoadmapController } from './roadmap/controllers/RoadmapController';
import { SignInController } from './signin/controllers/SignInController';
import { SignOutController } from './signout/controllers/SignOutController';
import { SignUpController } from './signup/controllers/SignUpController';
import { VerifyAccreditationController } from './verify-accreditation/controllers/VerifyAccreditationController';
import { VerifyEmailController } from './verify-email/controllers/VerifyEmailController';
import { WaitListController } from './waitlist/controllers/WaitlistController';
import { CashFlowOfferingController } from './offerings/cashflow/controllers/CashFlowOfferingController';

const RouterErrorBoundary: React.FC = () => {
  const err = useRouteError() as unknown as Error;
  logging.logError(err, { context: 'RouterErrorBoundary' });
  return <ErrorPage err={err} />;
};

export const Router: React.FC = () => {
  const [isLoading, setisLoading] = useState(true);
  const location = useLocation();
  const errEl = <RouterErrorBoundary />;

  useEffect(() => {
    tracking.page();
    security.trackPageView();
  }, [location]);

  useEffect(() => {
    async function handlePageLoad() {
      if (
        location.pathname !== ROUTES.createAddress &&
        location.pathname !== ROUTES.createCitizenship &&
        location.pathname !== ROUTES.createReviewCreateAccount &&
        location.pathname !== ROUTES.signup &&
        location.pathname !== ROUTES.signin &&
        location.pathname !== ROUTES.signout &&
        location.pathname !== ROUTES.resetPassword &&
        location.pathname !== ROUTES.waitlist &&
        location.pathname !== ROUTES.waitlistStatus
      ) {
        await ConsolidatedModel.getState().fetchData();
      }
      await logging.start();
      await tracking.start();
      await support.start();
      await security.start();
      await params.start();
      setisLoading(false);
    }

    handlePageLoad();
  }, []);

  if (isLoading) return <FullScreenLoader />;

  return (
    <Routes>
      <Route element={<InboxController page="AGREEMENT" />} path={ROUTES.inboxAgreementAlert} errorElement={errEl} />
      <Route
        element={<InboxController page="VERIFY_EMAIL" />}
        path={ROUTES.inboxVerifyEmailAlert}
        errorElement={errEl}
      />
      <Route element={<InboxController page="DWOLLA_TERMS" />} path={ROUTES.inboxDwollaAlert} errorElement={errEl} />
      <Route element={<InboxController page="LIST" />} path={ROUTES.inbox} errorElement={errEl} />
      <Route element={<InboxController page="MESSAGE" />} path={ROUTES.inboxMessage(':id')} errorElement={errEl} />
      <Route element={<AccountInformationController />} path={ROUTES.accountInformation} errorElement={errEl} />
      <Route element={<ActivityController />} path={ROUTES.activity} errorElement={errEl} />
      <Route element={<AutoInvestmentsController />} path={ROUTES.autoInvestSchedule} errorElement={errEl} />
      <Route element={<BankAccountsController />} path={ROUTES.bankAccounts} errorElement={errEl} />
      <Route element={<CheckoutCashflowController />} path={ROUTES.checkoutCashflow} errorElement={errEl} />
      <Route element={<CheckoutSFRController />} path={ROUTES.checkoutSFR(':id')} errorElement={errEl} />
      <Route element={<CashFlowOfferingController />} path={ROUTES.offeringsCashflow} errorElement={errEl} />
      <Route element={<DashboardController />} path={ROUTES.dashboard} errorElement={errEl} />
      <Route element={<StatementController />} path={ROUTES.statement(':month')} errorElement={errEl} />
      <Route element={<DocumentsController />} path={ROUTES.documents} errorElement={errEl} />
      <Route element={<ErrorController />} path={ROUTES.error} errorElement={errEl} />
      <Route element={<ForbiddenPage />} path={ROUTES.forbidden} errorElement={errEl} />
      <Route element={<InvestorProfileController />} path={ROUTES.profile} errorElement={errEl} />
      <Route element={<PropertyController page="dashboard" />} path={ROUTES.properties} errorElement={errEl} />
      <Route
        element={<PropertyController page="detail" />}
        path={ROUTES.propertyDetail(':slug')}
        errorElement={errEl}
      />
      <Route element={<SignInController page="reset-password" />} path={ROUTES.resetPassword} errorElement={errEl} />
      <Route element={<SignInController page="signin" />} path={ROUTES.signin} errorElement={errEl} />
      <Route element={<SignOutController />} path={ROUTES.signout} errorElement={errEl} />
      <Route element={<SignUpController />} path={ROUTES.signup} errorElement={errEl} />
      <Route element={<VerifyEmailController />} path={ROUTES.verifyEmail} errorElement={errEl} />
      <Route element={<VerifyAccreditationController />} path={ROUTES.verifyAccreditation} errorElement={errEl} />
      <Route element={<CreateAccountController page={'ADDRESS'} />} path={ROUTES.createAddress} errorElement={errEl} />
      <Route
        element={<CreateAccountController page={'CITIZENSHIP'} />}
        path={ROUTES.createCitizenship}
        errorElement={errEl}
      />
      <Route
        element={<CreateAccountController page={'REVIEW'} />}
        path={ROUTES.createReviewCreateAccount}
        errorElement={errEl}
      />
      <Route element={<RoadmapController />} path={ROUTES.roadmap} errorElement={errEl} />
      <Route element={<WaitListController page={'main'} />} path={ROUTES.waitlist} errorElement={errEl} />
      <Route element={<WaitListController page={'status'} />} path={ROUTES.waitlistStatus} errorElement={errEl} />
      <Route element={<SignInController page="signin" />} path={ROUTES.signin} errorElement={errEl} />
      <Route element={<PartnerSignInController />} path={ROUTES.partnerSignIn} errorElement={errEl} />
      <Route element={<PageNotFound />} path={'*'} errorElement={errEl} />
    </Routes>
  );
};
